import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SmartOrpcClientService } from './smart-orpc-client.service';
import { contractOutput } from '#backend/contract';

type Organization = contractOutput['organization']['list']['items'][number];

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private smartClient = inject(SmartOrpcClientService);

  private organizationsCache = new BehaviorSubject<Organization[] | null>(null);
  private cacheTimestamp: number | null = null;
  private readonly CACHE_DURATION_MS = 3600000; // 1 hora

  /**
   * Obtém todas as organizações (com cache)
   */
  async getOrganizations(
    forceRefresh = false,
    onlyWithActiveContracts = false,
  ): Promise<Organization[]> {
    const now = Date.now();
    const isCacheValid =
      this.cacheTimestamp && now - this.cacheTimestamp < this.CACHE_DURATION_MS;

    if (
      !forceRefresh &&
      isCacheValid &&
      this.organizationsCache.value &&
      !onlyWithActiveContracts
    ) {
      return this.organizationsCache.value;
    }

    try {
      const response = await this.smartClient.client.organization.list({
        onlyWithActiveContracts,
      });
      const organizations = response.items;

      if (!onlyWithActiveContracts) {
        this.organizationsCache.next(organizations);
        this.cacheTimestamp = now;
      }

      return organizations;
    } catch (error) {
      console.error('Erro ao carregar organizações:', error);
      throw error;
    }
  }

  /**
   * Observable para reagir a mudanças na lista de organizações
   */
  getOrganizations$(): Observable<Organization[] | null> {
    return this.organizationsCache.asObservable();
  }

  /**
   * Busca organizações por termo (client-side filtering)
   */
  async searchOrganizations(
    term: string,
    onlyWithActiveContracts = false,
  ): Promise<Organization[]> {
    const organizations = await this.getOrganizations(
      false,
      onlyWithActiveContracts,
    );

    if (!term || term.trim() === '') {
      return organizations;
    }

    const searchTerm = term.toLowerCase().trim();
    return organizations.filter((org) =>
      org.name.toLowerCase().includes(searchTerm),
    );
  }

  /**
   * Obtém uma organização por ID
   */
  async getOrganizationById(id: number): Promise<Organization | undefined> {
    const organizations = await this.getOrganizations();
    return organizations.find((org) => org.id === id);
  }

  /**
   * Invalida o cache forçando uma nova busca na próxima chamada
   */
  invalidateCache(): void {
    this.organizationsCache.next(null);
    this.cacheTimestamp = null;
  }
}
